import { useQuery } from '@tanstack/react-query';
import { Loader2, CheckCircle2, XCircle, Clock, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { documentChunkService } from '@/services';

interface VectorizationLogsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachmentId: string;
  attachmentName: string;
}

export default function VectorizationLogsDialog({
  open,
  onOpenChange,
  attachmentId,
  attachmentName,
}: VectorizationLogsDialogProps) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['vectorization-logs', attachmentId],
    queryFn: async () => {
      const result = await documentChunkService.listLogsByAttachment(attachmentId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!attachmentId,
  });

  const formatTimestamp = (iso: string) => {
    try {
      return format(parseISO(iso), 'MMM d, yyyy h:mm:ss a');
    } catch {
      return iso;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (ms == null) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Vectorization Logs</DialogTitle>
          <DialogDescription>
            {attachmentName} — {logs.length} log{logs.length !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No vectorization logs found for this attachment.
          </div>
        ) : (
          <ScrollArea className="h-[50vh]">
            <div className="space-y-3 pr-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="border border-border rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge
                      variant={log.status === 'success' ? 'default' : 'destructive'}
                      className={
                        log.status === 'success'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : ''
                      }
                    >
                      {log.status === 'success' ? (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      ) : (
                        <XCircle className="w-3 h-3 mr-1" />
                      )}
                      {log.status === 'success' ? 'Success' : 'Error'}
                    </Badge>
                    {formatDuration(log.processing_time_ms) && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(log.processing_time_ms)}
                      </span>
                    )}
                  </div>

                  {log.status === 'success' && (
                    <div className="flex gap-4 text-sm text-muted-foreground mb-2">
                      {log.chunks_created != null && (
                        <span>{log.chunks_created} chunks</span>
                      )}
                      {log.page_count != null && (
                        <span>{log.page_count} pages</span>
                      )}
                      {log.sections_extracted != null && (
                        <span>{log.sections_extracted} sections</span>
                      )}
                    </div>
                  )}

                  {log.status === 'error' && log.error_message && (
                    <pre className="text-xs bg-destructive/10 text-destructive rounded p-2 mb-2 whitespace-pre-wrap break-words font-mono">
                      {log.error_message}
                    </pre>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatTimestamp(log.created)}</span>
                    {log.triggered_by_profile?.full_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.triggered_by_profile.full_name}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
